import React from 'react'
import { useTranslation } from 'react-i18next'
import { CsvColumnConfig, CsvColumnProfile, CsvColumnValidation, CsvDataType, DecimalSeparator, ThousandsSeparator } from '../types/csv.types'
import { SUPPORTED_DATE_FORMATS } from '../utils/dateFormatDetector'

const TYPE_OPTIONS: CsvDataType[] = ['string', 'integer', 'float', 'boolean', 'date', 'datetime']
const DECIMAL_OPTIONS: { label: string; value: DecimalSeparator }[] = [
    { label: 'Dot ( . )', value: '.' },
    { label: 'Comma ( , )', value: ',' },
]
const THOUSANDS_OPTIONS: { label: string; value: ThousandsSeparator }[] = [
    { label: '—', value: null },
    { label: ',', value: ',' },
    { label: '.', value: '.' },
    { label: 'Space', value: ' ' },
]
const DATE_FORMAT_OPTIONS: string[] = [...SUPPORTED_DATE_FORMATS]

interface Props {
    profile: { columns: CsvColumnProfile[] }
    configs: CsvColumnConfig[]
    validations?: CsvColumnValidation[]
    onChange: (configs: CsvColumnConfig[]) => void
}

const CsvColumnConfiguration: React.FC<Props> = ({ profile, configs, validations, onChange }) => {
    const { t } = useTranslation()

    const updateConfig = (index: number, patch: Partial<CsvColumnConfig>) => {
        const next = configs.map((c, i) => (i === index ? { ...c, ...patch } : c))
        onChange(next)
    }

    const validationByColumn = new Map<string, CsvColumnValidation>()
    for (const v of validations ?? []) validationByColumn.set(v.columnName, v)

    const isNumeric = (t2: CsvDataType) => t2 === 'integer' || t2 === 'float'
    const isDateLike = (t2: CsvDataType) => t2 === 'date' || t2 === 'datetime'

    return (
        <div className="overflow-x-auto max-h-[16rem] border border-slate-200 rounded">
            <table className="min-w-full text-[0.6rem]">
                <thead className="bg-slate-100 sticky top-0">
                    <tr>
                        <th className="p-1 text-left">{t('Column')}</th>
                        <th className="p-1 text-left">{t('Detected')}</th>
                        <th className="p-1 text-left">{t('Type')}</th>
                        <th className="p-1 text-left">{t('Decimal')}</th>
                        {/* <th className="p-1 text-left">{t('Thousands')}</th> */}
                        <th className="p-1 text-left">{t('Date Format')}</th>
                        <th className="p-1 text-left">{t('Null %')}</th>
                        <th className="p-1 text-left">{t('Unique')}</th>
                        <th className="p-1 text-left">{t('Invalid')}</th>
                    </tr>
                </thead>
                <tbody>
                    {profile.columns.map((col, index) => {
                        const config = configs[index]
                        if (!config) return null
                        const validation = validationByColumn.get(col.name)
                        const invalidCount = validation?.invalidCount ?? 0
                        return (
                            <tr key={col.name + index} className="border-t border-slate-100 hover:bg-[#E5B61112] transition-colors">
                                <td className="p-1 font-medium truncate max-w-[7rem]" title={col.name}>
                                    {col.name}
                                </td>
                                <td className="p-1 text-slate-500">{col.detectedType}</td>
                                <td className="p-1">
                                    <select
                                        aria-label={`${t('Type')}: ${col.name}`}
                                        className="border border-slate-300 rounded px-1"
                                        value={config.selectedType}
                                        onChange={(e) => updateConfig(index, { selectedType: e.target.value as CsvDataType, typeSource: 'manual' })}
                                    >
                                        {TYPE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="p-1">
                                    {isNumeric(config.selectedType) ? <span>{DECIMAL_OPTIONS.find((option) => option.value === config.decimalSeparator)?.label}</span> : <span className="text-slate-300">—</span>}
                                    {/* {isNumeric(config.selectedType) ? (
                                        <select
                                            aria-label={`${t('Decimal')}: ${col.name}`}
                                            className="border border-slate-300 rounded px-1"
                                            value={config.decimalSeparator ?? '.'}
                                            disabled={true}
                                            onChange={(e) => updateConfig(index, { decimalSeparator: e.target.value as DecimalSeparator })}
                                        >
                                            {DECIMAL_OPTIONS.map((option) => (
                                                <option key={option.label} value={option.value ?? ''}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )} */}
                                </td>
                                {/* <td className="p-1">
                                    {isNumeric(config.selectedType) ? (
                                        <select
                                            aria-label={`${t('Thousands')}: ${col.name}`}
                                            className="border border-slate-300 rounded px-1"
                                            value={config.thousandsSeparator ?? ''}
                                            onChange={(e) => {
                                                const raw = e.target.value
                                                const next: ThousandsSeparator = raw === '' ? null : (raw as ThousandsSeparator)
                                                updateConfig(index, { thousandsSeparator: next })
                                            }}
                                        >
                                            {THOUSANDS_OPTIONS.map((option) => (
                                                <option key={option.label} value={option.value ?? ''}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td> */}
                                <td className="p-1">
                                    {isDateLike(config.selectedType) ? (
                                        <select aria-label={`${t('Date Format')}: ${col.name}`} className="border border-slate-300 rounded px-1" value={config.dateFormat ?? ''} onChange={(e) => updateConfig(index, { dateFormat: e.target.value })}>
                                            <option value="">--</option>
                                            {DATE_FORMAT_OPTIONS.map((format) => (
                                                <option key={format} value={format}>
                                                    {format}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                                <td className={`p-1 ${col.nullPercentage > 0 ? 'text-red-600 font-semibold' : ''}`}>{col.nullPercentage.toFixed(1)}%</td>
                                <td className="p-1">{col.isUnique ? <span className="text-green-600 font-semibold">{t('Yes')}</span> : t('No')}</td>
                                <td className="p-1">
                                    {invalidCount > 0 ? (
                                        <span className="text-red-600" title={validation?.invalidExamples.join(', ')}>
                                            {invalidCount}
                                        </span>
                                    ) : (
                                        <span className="text-slate-400">0</span>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default CsvColumnConfiguration
